import React from "react";
import { FormGroup, Input, Label } from "reactstrap";

import { SHIPPING_FEE_OVERRIDE_REASON_MAX } from "./shippingPolicy";
import { formatMoney } from "./salesUtils";

/**
 * A2 delivery fee for a policy-priced order (order wizard and edit modal).
 *
 * The fee shown is the policy fee for the destination and the products total; staff never
 * type it. A manager can tick "override" to set another amount, always with a reason; the
 * server checks the role, keeps the reason in the order history and recalculates the total.
 */
export default function ShippingFeeControl({
  t,
  idPrefix,
  classPrefix,
  policyFee,
  override,
  onChange,
  errors = {},
  showErrors = false,
  currencyCode,
}) {
  const update = (patch) => onChange({ ...override, ...patch });
  const amountError = showErrors ? errors.feeOverrideAmount : null;
  const reasonError = showErrors ? errors.feeOverrideReason : null;

  return (
    <div className={`${classPrefix}-shipping-policy`}>
      <div className={`${classPrefix}-summary-row`}>
        <span>{t("storefrontOrders.shippingPolicyFee")}</span>
        <strong data-testid={`${idPrefix}-policy-fee`}>
          {policyFee === null
            ? t("storefrontOrders.shippingCountryUnsupported")
            : policyFee === 0
              ? t("storefrontOrders.shippingFeeFree")
              : formatMoney(policyFee, currencyCode)}
        </strong>
      </div>
      <small className="form-text text-muted mb-2">{t("storefrontOrders.shippingPolicyHint")}</small>

      <FormGroup check className="mb-0">
        <Label check for={`${idPrefix}-override`}>
          <Input
            id={`${idPrefix}-override`}
            type="checkbox"
            checked={override.enabled === true}
            onChange={(event) => update({ enabled: event.target.checked })}
          />
          <span className="form-check-sign">
            <span className="check" />
          </span>
          {t("storefrontOrders.shippingFeeOverride")}
        </Label>
        <small className="form-text text-muted">{t("storefrontOrders.shippingFeeOverrideHint")}</small>
      </FormGroup>

      {override.enabled ? (
        <div className="mt-3">
          <FormGroup>
            <Label for={`${idPrefix}-override-amount`}>{t("storefrontOrders.shippingFeeOverrideAmount")}</Label>
            <div className={`${classPrefix}-money-input`}>
              <Input
                id={`${idPrefix}-override-amount`}
                type="number"
                min="0"
                step="0.01"
                value={override.amount}
                invalid={Boolean(amountError)}
                onChange={(event) => update({ amount: event.target.value })}
              />
              <span className={`${classPrefix}-money-suffix`} aria-hidden="true">
                €
              </span>
            </div>
            {amountError ? <p className={`${classPrefix}-field-error`}>{t(amountError)}</p> : null}
          </FormGroup>
          <FormGroup className="mb-0">
            <Label for={`${idPrefix}-override-reason`}>{t("storefrontOrders.shippingFeeOverrideReason")}</Label>
            <Input
              id={`${idPrefix}-override-reason`}
              type="textarea"
              rows={2}
              maxLength={SHIPPING_FEE_OVERRIDE_REASON_MAX}
              value={override.reason}
              invalid={Boolean(reasonError)}
              onChange={(event) => update({ reason: event.target.value })}
            />
            {reasonError ? <p className={`${classPrefix}-field-error`}>{t(reasonError)}</p> : null}
          </FormGroup>
        </div>
      ) : null}
    </div>
  );
}
